import { Layout } from "../components/Layout";
import { BackHome, SectionHeader, StatBlock, ProcessCard, FeatureCard, GreenCta } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import { ArrowRight, MessageCircle, Search, Zap, FileText, Calendar, MessageSquare, Check } from "lucide-react";
import { WHATSAPP_LINK } from "../lib/api";
import { ContactForm } from "../components/ContactForm";

const stats = [
  { value: "60 min", label: "Durée de l'audit" },
  { value: "0", label: "Jargon technique" },
  { value: "3", label: "Processus analysés" },
  { value: "100%", label: "Gratuit & sans engagement" },
];

const livrables = [
  { num: "01", Icon: Search, title: "Cartographie", text: "Vos 3 processus les plus chronophages identifiés et priorisés par ROI." },
  { num: "02", Icon: Zap, title: "Recommandations", text: "Pour chaque processus : l'outil IA adapté, le délai de déploiement, et le gain estimé." },
  { num: "03", Icon: FileText, title: "Plan d'action", text: "Un rapport écrit que vous gardez — que vous travailliez avec moi ou non." },
];

const pourQui = [
  "Vous répondez aux mêmes emails chaque semaine",
  "Vos outils ne se parlent pas entre eux",
  "Vous manquez des appels ou des demandes clients",
  "Vos rapports se font encore à la main",
  "Vous voulez automatiser mais ne savez pas par où commencer",
  "Vous avez déjà testé un outil IA sans résultat concret",
];

const Audit = () => {
  useSeo({
    title: "Audit IA Gratuit pour PME — Genève | Consulting IA",
    description: "En 45 minutes, j'identifie les 3 tâches que votre PME peut automatiser immédiatement. Audit offert, sans engagement, disponible à Genève.",
    path: "/audit-ia",
  });

  return (
    <Layout>
      <BackHome />

      <section className="px-6 pt-10 pb-12 md:pt-16 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-5">🔍 Audit IA gratuit</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            Audit IA offert : découvrez ce que vous pouvez{" "}
            <span className="accent-text">automatiser en 45 min</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            On identifie ensemble vos 3 processus les plus automatisables — et vous repartez
            avec un plan concret. Gratuit. Sans engagement.
          </p>
          <div className="flex flex-wrap gap-2 mt-7 mb-9 justify-center">
            <span className="pill-badge">🇨🇭 Basé à Genève</span>
            <span className="pill-badge">⚡ Réponse sous 24h</span>
            <span className="pill-badge">📅 Limité à 4/mois</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap justify-center">
            <a href="#reserver" className="btn-primary" data-testid="audit-cta">
              Réserver mon audit <ArrowRight size={18} />
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto fade-in">
          <StatBlock stats={stats} testId="audit-stats" />
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Livrable" title="Vous repartez avec" accentTitle="un plan d'action." />
          <div className="grid md:grid-cols-3 gap-6">
            {livrables.map((l) => <FeatureCard key={l.num} {...l} />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Le processus" title="Trois étapes." accentTitle="Aucun effort." />
          <div className="grid md:grid-cols-3 gap-6">
            <ProcessCard num="01" badge="5 minutes" Icon={Calendar} title="Vous réservez" text="Un créneau de 60 min en ligne ou à Genève. Je confirme sous 24h." />
            <ProcessCard num="02" badge="60 minutes" Icon={MessageSquare} title="On se parle" text="Je pose les bonnes questions sur votre activité. Vous n'avez rien à préparer." />
            <ProcessCard num="03" badge="48h après" Icon={FileText} title="Vous recevez le plan" text="Rapport écrit avec les 3 opportunités identifiées et les prochaines étapes." />
          </div>
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            badge="Cible"
            title="Fait pour les"
            accentTitle="PME suisses."
            subtitle="Si vous reconnaissez une de ces situations, l'audit est fait pour vous."
          />
          <div className="grid md:grid-cols-2 gap-4">
            {pourQui.map((p, i) => (
              <div key={i} className="card-base flex items-start gap-3 fade-in">
                <span className="icon-square !w-10 !h-10 !rounded-lg shrink-0">
                  <Check size={18} />
                </span>
                <p className="title-text font-medium text-sm pt-2">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <SectionHeader badge="Transparence" title="Gratuit." accentTitle="Vraiment." />
          <div className="rounded-3xl p-10 md:p-14 fade-in" style={{ background: "linear-gradient(135deg,#3ecf8e 0%,#2db97a 100%)" }}>
            <span className="pill-badge-dark mb-5">Mon modèle</span>
            <p className="text-white text-lg leading-relaxed mb-6">
              Je propose cet audit gratuitement parce que je sais qu'une fois que vous voyez ce que l'IA peut faire pour votre entreprise — concrètement, chiffres à l'appui — la conversation suivante se fait naturellement. Mais si vous repartez avec le plan et le faites seul, c'est bien aussi.
            </p>
            <p className="text-white font-bold">— Christophe Battras</p>
          </div>
        </div>
      </section>

      <section id="reserver" className="px-6 py-16 section-alt scroll-mt-24">
        <div className="max-w-3xl mx-auto">
          <SectionHeader badge="Réservation" title="Réservez" accentTitle="votre audit." subtitle="4 créneaux disponibles par mois. Premier arrivé, premier servi." />
          <ContactForm source="audit" page="/audit-ia" buttonLabel="Réserver mon audit" />
        </div>
      </section>

      <GreenCta
        badge="Prochaine étape"
        title="Réservez votre audit."
        subtitle="4 créneaux disponibles par mois. Premier arrivé, premier servi."
        primaryLabel={<>Réserver maintenant <ArrowRight size={18} /></>}
        primaryAction={() => document.getElementById("reserver")?.scrollIntoView({ behavior: "smooth" })}
        secondaryLabel="WhatsApp"
        secondaryAction={WHATSAPP_LINK}
        footnotes={["Gratuit", "Sans engagement", "Basé à Genève"]}
      />

      <SeoText>
        <h2 className="section-title mb-6">
          Comment se déroule <span className="accent-text">un audit IA</span> avec moi
        </h2>
        <p className="body-text leading-relaxed mb-5">
          L'audit IA gratuit est conçu pour les dirigeants de PME genevoises et suisses
          romandes qui sentent qu'il y a quelque chose à automatiser dans leur activité,
          mais qui ne savent pas par où commencer. C'est volontairement court — 60
          minutes — parce que ce n'est pas un cours d'IA : c'est une conversation
          structurée sur votre métier, vos processus, et les endroits où l'IA peut
          réellement créer de la valeur dans votre quotidien.
        </p>
        <p className="body-text leading-relaxed mb-5">
          La séance se déroule à Genève (rue préférée, café, ou directement dans vos
          locaux) ou en visioconférence si vous êtes hors arc lémanique. Vous n'avez
          strictement rien à préparer. Je pose les bonnes questions sur votre activité,
          vos équipes, vos outils actuels, vos points de friction, vos volumes
          d'opérations, vos pertes de temps connues. À partir de là, je décrypte avec
          vous les trois processus qui ont le meilleur retour sur investissement à
          automatiser dans les six prochains mois.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Dans les 48 heures qui suivent, je vous envoie un rapport écrit personnalisé.
          Pour chacune des trois opportunités identifiées : la description du processus,
          la technologie IA recommandée, l'effort de mise en place estimé (en jours
          homme), le gain attendu (en heures par semaine ou en CHF), et les prochaines
          étapes concrètes. Vous gardez ce rapport, vous en faites ce que vous voulez —
          que vous travailliez avec moi ou pas.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Pourquoi gratuit ? Parce que c'est mon modèle. Je sais qu'une fois que vous
          voyez ce que l'IA peut faire concrètement pour votre entreprise, avec des
          chiffres à l'appui et un plan clair, la conversation suivante se fait
          naturellement. Et si vous décidez de tout faire seul ensuite, c'est très bien
          aussi — l'audit aura quand même fait son travail.
        </p>
        <p className="body-text leading-relaxed">
          L'audit est limité à 4 séances par mois pour préserver la qualité. La majorité
          des dirigeants qui le font repartent avec au moins une idée applicable
          immédiatement, et plus de 70 % décident ensuite de déployer au moins un des
          trois services proposés. Réservez votre créneau via le formulaire ci-dessus,
          ou écrivez-moi directement sur WhatsApp pour échanger en amont.
        </p>
      </SeoText>
    </Layout>
  );
};

export default Audit;
